import { Card, CardContent, Typography, Box, Grid, CircularProgress, Chip } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { fetchFeatureUsageStats as fetchFeatureUsageStatsRequest } from '../../services/dashboard.service';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useFilterContext } from '../../usageandbilling/FilterContext';

interface TopUser {
  userId: string;
  name: string;
  email: string;
  count: number;
}

interface FeatureUsageData {
  featureUsageSummary: {
    transcriptions: { count: number; percentageChange: number | null };
    transcriptionDuration: { hours: number; percentageChange: number | null };
    factFinds: { count: number; percentageChange: number | null };
    meetings: { count: number; percentageChange: number | null };
    calendlyMeetings?: { count: number; percentageChange: number | null };
    clients: { count: number; percentageChange: number | null };
    clientsAssigned: { count: number; percentageChange: number | null };
    documents: { count: number; percentageChange: number | null };
    aiAgentUsage: { count: number; percentageChange: number | null };
    contacts: { count: number; percentageChange: number | null };
    fileNotesGenerated: { count: number; percentageChange: number | null };
    fileNotesRegenerated: { count: number; percentageChange: number | null; regenerationRatio: number };
    powerPointsGenerated: { count: number; percentageChange: number | null };
    emailsGenerated: { count: number; percentageChange: number | null };
    emailsSent: { count: number; percentageChange: number | null; conversionRate: number };
    smsSent: { count: number; percentageChange: number | null; segments: number };
  };
  featureDistribution: Array<{ name: string; value: number }>;
  topUsers: {
    transcriptions: TopUser[];
    factFinds: TopUser[];
    meetings: TopUser[];
    calendlyMeetings?: TopUser[];
    clients: TopUser[];
    documents: TopUser[];
    aiAgentUsage: TopUser[];
    contacts: TopUser[];
    fileNotesGenerated: TopUser[];
    powerPointsGenerated: TopUser[];
    emailsGenerated: TopUser[];
    emailsSent: TopUser[];
    smsSent: TopUser[];
  };
}

interface EnrichedFeatureData {
  name: string;
  value: number;
  topUsers: TopUser[];
}

// Color palette for different feature types
const FEATURE_COLORS: { [key: string]: string } = {
  'Transcriptions': '#1976D2',
  'Fact Finds': '#388E3C',
  'Meetings': '#F57C00',
  'Calendly': '#00a2ff',
  'Clients': '#7B1FA2',
  'Documents': '#0097A7',
  'AI Agent': '#C62828',
  'Contacts': '#5D4037',
  'File Notes': '#00796B',
  'PowerPoints': '#303F9F',
  'Emails Sent': '#E64A19',
  'SMS Sent': '#FF5722'
};

// Mapping between display names and API keys
const FEATURE_KEY_MAP: { [displayName: string]: string } = {
  'Transcriptions': 'transcriptions',
  'Fact Finds': 'factFinds',
  'Meetings': 'meetings',
  'Calendly': 'calendlyMeetings',
  'Clients': 'clients',
  'Documents': 'documents',
  'AI Agent': 'aiAgentUsage',
  'Contacts': 'contacts',
  'File Notes': 'fileNotesGenerated',
  'PowerPoints': 'powerPointsGenerated',
  'Emails Sent': 'emailsSent',
  'SMS Sent': 'smsSent'
};

// Custom tooltip component for feature usage drill-down
const FeatureUsageTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload as EnrichedFeatureData;
  const topUsers = data.topUsers?.slice(0, 5) || [];

  return (
    <Box sx={{
      p: 2,
      bgcolor: 'background.paper',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      minWidth: 220,
    }}>
      <Typography variant="body2" color="text.secondary" fontWeight="medium">
        {data.name}
      </Typography>
      <Typography variant="h5" fontWeight="medium" my={1}>
        {data.value.toLocaleString()} uses
      </Typography>

      {topUsers.length > 0 ? (
        <Box mt={2}>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            Top Users
          </Typography>
          {topUsers.map((user: TopUser, index: number) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: index > 0 ? 0.5 : 0 }}
            >
              <Typography
                variant="body2"
                noWrap
                title={user.name}
                sx={{ maxWidth: 140 }}
              >
                {user.name}
              </Typography>
              <Chip
                label={user.count}
                size="small"
                sx={{ bgcolor: '#f1f5f9', ml: 1, minWidth: 45 }}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" mt={1}>
          No user data available
        </Typography>
      )}
    </Box>
  );
};

const FeatureUsageCard = () => {
  const { selectedOrgIds, selectedUserIds, dateRange } = useFilterContext();
  const [data, setData] = useState<FeatureUsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchFeatureUsageStats = async () => {
      if (!dateRange[0] || !dateRange[1]) return;

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          startTime: dateRange[0].startOf('day').toISOString(),
          endTime: dateRange[1].endOf('day').toISOString(),
        });
        if (selectedOrgIds.length > 0) params.set('orgIds', selectedOrgIds.join(','));
        if (selectedUserIds.length > 0) params.set('userIds', selectedUserIds.join(','));

        const response = await fetchFeatureUsageStatsRequest(params.toString(), controller.signal);
        setData(response as FeatureUsageData);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        console.error('Failed to fetch feature usage stats:', err);
        setError('Failed to load feature usage statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchFeatureUsageStats();
    return () => controller.abort();
  }, [dateRange, selectedOrgIds, selectedUserIds]);

  const transformedChartData = useMemo<EnrichedFeatureData[]>(() => {
    if (!data?.featureDistribution || !data?.topUsers) return [];

    return data.featureDistribution.map(feature => ({
      name: feature.name,
      value: feature.value,
      topUsers: data.topUsers[FEATURE_KEY_MAP[feature.name] as keyof typeof data.topUsers] || []
    }));
  }, [data]);

  const formatDuration = (hours: number) => {
    if (!hours && hours !== 0) return null;
    return hours < 1
      ? `${Math.round(hours * 60)} mins`
      : `${hours.toFixed(1)} hrs`;
  };

  const renderStatItem = (
    label: string,
    value: string | number,
    percentageChange: number | null,
    color: string,
    subValue?: string | null
  ) => {
    const isPositive = percentageChange !== null && percentageChange >= 0;
    const isInfinite = percentageChange === Infinity;
    const hasChange = percentageChange !== null && isFinite(percentageChange);

    return (
      <Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}>
            {label}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
          <Typography variant="h6" fontWeight="medium">
            {value}
          </Typography>

          {subValue && (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5, fontWeight: 500 }}>
              ({subValue})
            </Typography>
          )}

          {(hasChange || isInfinite) && (
            <Box
              sx={{
                bgcolor: isInfinite ? '#E6F4EA' : (isPositive ? '#E6F4EA' : '#FDEDED'),
                color: isInfinite ? '#2E7D32' : (isPositive ? '#2E7D32' : '#C62828'),
                borderRadius: '6px',
                px: 0.75,
                py: 0.25,
                ml: subValue ? 0 : 'auto'
              }}
            >
              <Typography variant="caption" fontWeight="bold">
                {isInfinite ? '+100%' : `${isPositive ? '+' : ''}${percentageChange!.toFixed(1)}%`}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card sx={{ p: 2, height: '100%' }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Feature Usage Statistics
          </Typography>
          <Typography color="error">{error || 'No data available'}</Typography>
        </CardContent>
      </Card>
    );
  }

  const { featureUsageSummary } = data;

  return (
    <Card sx={{ p: 2, height: '100%' }}>
      <CardContent>
        <Box mb={3}>
          <Typography variant="h6" fontWeight="bold">
            Feature Usage Statistics
          </Typography>
        </Box>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'Transcriptions',
              featureUsageSummary.transcriptions.count.toLocaleString(),
              featureUsageSummary.transcriptions.percentageChange,
              FEATURE_COLORS['Transcriptions'],
              formatDuration(featureUsageSummary.transcriptionDuration.hours)
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'Fact Finds',
              featureUsageSummary.factFinds.count.toLocaleString(),
              featureUsageSummary.factFinds.percentageChange,
              FEATURE_COLORS['Fact Finds']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'Meetings',
              featureUsageSummary.meetings.count.toLocaleString(),
              featureUsageSummary.meetings.percentageChange,
              FEATURE_COLORS['Meetings']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'Calendly',
              (featureUsageSummary.calendlyMeetings?.count ?? 0).toLocaleString(),
              featureUsageSummary.calendlyMeetings?.percentageChange ?? null,
              FEATURE_COLORS['Calendly']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'Clients',
              featureUsageSummary.clients.count.toLocaleString(),
              featureUsageSummary.clients.percentageChange,
              FEATURE_COLORS['Clients']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'Documents',
              featureUsageSummary.documents.count.toLocaleString(),
              featureUsageSummary.documents.percentageChange,
              FEATURE_COLORS['Documents']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'AI Agent Usage',
              featureUsageSummary.aiAgentUsage.count.toLocaleString(),
              featureUsageSummary.aiAgentUsage.percentageChange,
              FEATURE_COLORS['AI Agent']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'Contacts',
              featureUsageSummary.contacts.count.toLocaleString(),
              featureUsageSummary.contacts.percentageChange,
              FEATURE_COLORS['Contacts']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'File Notes',
              featureUsageSummary.fileNotesGenerated.count.toLocaleString(),
              featureUsageSummary.fileNotesGenerated.percentageChange,
              FEATURE_COLORS['File Notes']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'PowerPoints',
              featureUsageSummary.powerPointsGenerated.count.toLocaleString(),
              featureUsageSummary.powerPointsGenerated.percentageChange,
              FEATURE_COLORS['PowerPoints']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'Emails Sent',
              featureUsageSummary.emailsSent.count.toLocaleString(),
              featureUsageSummary.emailsSent.percentageChange,
              FEATURE_COLORS['Emails Sent']
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            {renderStatItem(
              'SMS Sent',
              (featureUsageSummary.smsSent?.count || 0).toLocaleString(),
              featureUsageSummary.smsSent?.percentageChange || null,
              FEATURE_COLORS['SMS Sent'],
              `${featureUsageSummary.smsSent?.segments || 0} segments`
            )}
          </Grid>
        </Grid>

        {transformedChartData.length > 0 && (
          <Box
            sx={{
              bgcolor: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
              borderRadius: '16px',
              p: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #e8eaf0'
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" mb={3} color="text.primary" sx={{ fontSize: '0.95rem' }}>
              Feature Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={380}>
              <BarChart
                data={transformedChartData}
                margin={{ top: 20, right: 30, left: 10, bottom: 85 }}
                barGap={12}
              >
                <defs>
                  {Object.entries(FEATURE_COLORS).map(([name, color]) => (
                    <linearGradient key={name} id={`colorGradient-${name.replace(/\s+/g, '-')}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={1} />
                      <stop offset="95%" stopColor={color} stopOpacity={0.85} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e8eaf0"
                  vertical={false}
                  strokeOpacity={0.5}
                />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 11, fill: '#64748b', fontWeight: 500 }}
                  tickLine={false}
                  axisLine={{ stroke: '#cbd5e1', strokeWidth: 1.5 }}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip
                  content={<FeatureUsageTooltip />}
                  cursor={{
                    fill: 'rgba(148, 163, 184, 0.08)',
                    radius: 8
                  }}
                  wrapperStyle={{ zIndex: 1000, outline: 'none' }}
                />
                <Bar
                  dataKey="value"
                  name="Usage Count"
                  radius={[12, 12, 0, 0]}
                  maxBarSize={50}
                  animationDuration={800}
                  animationBegin={0}
                >
                  {transformedChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={`url(#colorGradient-${entry.name.replace(/\s+/g, '-')})`}
                      style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FeatureUsageCard;
